// The Daily Briefing's spend-vs-plan chart — the Performance report's trend
// chart idiom at card height: an area and line in the accent, a dashed
// reference line in ink, a crosshair on the hovered day and a hover card
// with that day's figures. Two series share one 0-based scale so the gap
// between spend and plan is honest. Nothing here is red: red is for a
// problem, and a day of spend is not one (Gio, 2026-09-19).

import {
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { niceMax } from '@/components/charts';
import type { VisualSeries } from '@/api/types.gen';
import './briefing-trend-chart.css';

export interface BriefingTrendChartProps {
  /** One label per point, already spelled for the page ("Sep 4"). */
  buckets: ReadonlyArray<string>;
  /** The first series is the solid one; every other series is a dashed reference. */
  series: ReadonlyArray<VisualSeries>;
  /** `$` (values are whole dollars), `%`, or null for a plain count. */
  unit: string | null;
  /** What the hover card is a card of ("Spend vs plan, 14 days"). */
  label: string;
}

const HEIGHT = 120;
const PAD = { top: 10, right: 10, bottom: 20, left: 44 };

/** `$1,234` · `68%` · `1,234` — whole units, the spelling the page uses. */
export function spellTrendValue(unit: string | null, value: number): string {
  const whole = Math.round(value).toLocaleString('en-US');
  if (unit === '$') return (value < 0 ? '-$' : '$') + Math.abs(Math.round(value)).toLocaleString('en-US');
  if (unit === '%') return `${whole}%`;
  return whole;
}

function useChartWidth() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(640);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const measure = () => {
      const next = Math.round(container.getBoundingClientRect().width);
      if (next > 0) setWidth(next);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);
  return { containerRef, width };
}

export function BriefingTrendChart({ buckets, series, unit, label }: BriefingTrendChartProps) {
  const tooltipId = useId();
  const wrapperRef = useRef<HTMLElement>(null);
  const { containerRef, width } = useChartWidth();
  const [active, setActive] = useState<number | null>(null);

  const n = buckets.length;
  const first = series[0];
  if (!first || n === 0) return null;
  const all = series.flatMap((s) => s.values.slice(0, n));
  const top = niceMax(Math.max(...all, 0)) || 1;
  const innerWidth = Math.max(width - PAD.left - PAD.right, 1);
  const innerHeight = HEIGHT - PAD.top - PAD.bottom;
  const step = innerWidth / Math.max(n - 1, 1);
  const x = (index: number) => (n === 1 ? PAD.left + innerWidth / 2 : PAD.left + index * step);
  const y = (value: number) => PAD.top + (1 - Math.max(0, Math.min(1, value / top))) * innerHeight;
  const baseline = y(0);
  const pathOf = (values: ReadonlyArray<number>) =>
    values.slice(0, n).map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const ticks = [top, top / 2, 0];
  const xLabels = n <= 3 ? buckets.map((_, i) => i) : [0, Math.floor((n - 1) / 2), n - 1];

  const select = (index: number) => setActive(index);
  const onKeyDown = (event: ReactKeyboardEvent<SVGGElement>, index: number) => {
    let next: number | null = null;
    if (event.key === 'ArrowLeft') next = Math.max(index - 1, 0);
    if (event.key === 'ArrowRight') next = Math.min(index + 1, n - 1);
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = n - 1;
    if (event.key === 'Escape') {
      setActive(null);
      event.currentTarget.blur();
      event.preventDefault();
      return;
    }
    if (next === null) return;
    event.preventDefault();
    wrapperRef.current?.querySelector<SVGGElement>(`[data-trend-index="${next}"]`)?.focus();
    select(next);
  };
  const onPointerLeave = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.pointerType === 'touch') return;
    if (wrapperRef.current?.contains(document.activeElement)) return;
    setActive(null);
  };

  const activeValues = active === null ? null : series.map((s) => s.values[active] ?? null);
  const variance = activeValues && activeValues.length > 1 && activeValues[0] != null && activeValues[1] != null
    ? activeValues[0] - activeValues[1]
    : null;
  const tooltipStyle = active === null
    ? undefined
    : ({ '--briefing-trend-tooltip-x': `${(x(active) / width) * 100}%` } as CSSProperties);

  return (
    <figure
      ref={wrapperRef}
      className="briefing-trend"
      aria-label={label}
      onPointerLeave={onPointerLeave}
      onBlur={(event) => {
        if (event.relatedTarget && wrapperRef.current?.contains(event.relatedTarget as Node)) return;
        setActive(null);
      }}
    >
      <div className="briefing-trend__surface" ref={containerRef}>
        <svg className="briefing-trend__svg" viewBox={`0 0 ${width} ${HEIGHT}`} width={width} height={HEIGHT} role="group" aria-label={label}>
          <g className="briefing-trend__grid" aria-hidden="true">
            {ticks.map((t) => (
              <g key={t}>
                <line x1={PAD.left} x2={width - PAD.right} y1={y(t)} y2={y(t)}/>
                <text x={PAD.left - 6} y={y(t)} textAnchor="end" dominantBaseline="middle">{spellTrendValue(unit, t)}</text>
              </g>
            ))}
          </g>
          <g className="briefing-trend__x-axis" aria-hidden="true">
            {xLabels.map((i) => (
              <text key={i} x={x(i)} y={HEIGHT - 6} textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}>{buckets[i]}</text>
            ))}
          </g>
          <g className="briefing-trend__series" aria-hidden="true">
            {n > 1 && (
              <path
                className="briefing-trend__area"
                d={`${pathOf(first.values)} L${x(n - 1).toFixed(1)},${baseline} L${x(0).toFixed(1)},${baseline} Z`}
              />
            )}
            {series.map((s, i) => (
              <path key={s.name} className={i === 0 ? 'briefing-trend__line' : 'briefing-trend__reference'} d={pathOf(s.values)}/>
            ))}
          </g>
          {active !== null && activeValues && (
            <g className="briefing-trend__selection" aria-hidden="true">
              <line x1={x(active)} x2={x(active)} y1={PAD.top} y2={HEIGHT - PAD.bottom}/>
              {activeValues[0] != null && <circle cx={x(active)} cy={y(activeValues[0])} r="4"/>}
            </g>
          )}
          <g className="briefing-trend__hit-targets">
            {buckets.map((bucket, index) => {
              const left = index === 0 ? PAD.left : (x(index - 1) + x(index)) / 2;
              const right = index === n - 1 ? width - PAD.right : (x(index) + x(index + 1)) / 2;
              const words = series.map((s) => `${s.name} ${spellTrendValue(unit, s.values[index] ?? 0)}`).join(', ');
              return (
                <g
                  key={`${bucket}-${index}`}
                  role="button"
                  tabIndex={0}
                  aria-pressed={active === index}
                  aria-describedby={active === index ? tooltipId : undefined}
                  aria-keyshortcuts="ArrowLeft ArrowRight Home End Escape"
                  aria-label={`${bucket}. ${words}.`}
                  data-trend-index={index}
                  onFocus={() => select(index)}
                  onPointerEnter={() => select(index)}
                  onPointerMove={() => select(index)}
                  onPointerDown={(event) => {
                    select(index);
                    if (event.pointerType !== 'touch') event.currentTarget.focus();
                  }}
                  onKeyDown={(event) => onKeyDown(event, index)}
                >
                  <rect x={left} y={PAD.top} width={Math.max(right - left, 1)} height={innerHeight}/>
                </g>
              );
            })}
          </g>
        </svg>
        {active !== null && activeValues && (
          <div
            id={tooltipId}
            className="briefing-trend__tooltip"
            data-side={active / Math.max(n - 1, 1) > 0.55 ? 'left' : 'right'}
            style={tooltipStyle}
            role="tooltip"
            aria-live="polite"
          >
            <div className="briefing-trend__tooltip-heading">
              <strong>{buckets[active]}</strong>
              <span>{label}</span>
            </div>
            <div className="briefing-trend__tooltip-grid">
              {series.map((s, i) => (
                <div key={s.name} className={`briefing-trend__tooltip-metric${i === 0 ? ' is-selected' : ''}`}>
                  <span>{s.name}</span>
                  <strong>{activeValues[i] == null ? '—' : spellTrendValue(unit, activeValues[i]!)}</strong>
                </div>
              ))}
              {variance !== null && (
                <div className="briefing-trend__tooltip-metric">
                  <span>vs plan</span>
                  <strong>{`${variance > 0 ? '+' : ''}${spellTrendValue(unit, variance)}`}</strong>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <figcaption className="briefing-trend__legend">
        {series.map((s, i) => (
          <span key={s.name} className="briefing-trend__key">
            <i className={i === 0 ? 'briefing-trend__swatch' : 'briefing-trend__swatch is-reference'}/>
            {s.name}
          </span>
        ))}
      </figcaption>
    </figure>
  );
}
